import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

interface BreadcrumbLabel {
  pathname: string;
  label: string;
}

interface BreadcrumbLabelContextValue {
  current: BreadcrumbLabel | null;
  setLabel: (pathname: string, label: string | null) => void;
}

const BreadcrumbLabelContext = createContext<BreadcrumbLabelContextValue | null>(null);

export function BreadcrumbLabelProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<BreadcrumbLabel | null>(null);

  const setLabel = useCallback((pathname: string, label: string | null) => {
    setCurrent((value) => {
      if (label) return { pathname, label };
      return value?.pathname === pathname ? null : value;
    });
  }, []);

  const value = useMemo(() => ({ current, setLabel }), [current, setLabel]);

  return (
    <BreadcrumbLabelContext.Provider value={value}>{children}</BreadcrumbLabelContext.Provider>
  );
}

export function useBreadcrumbLabel(label: string | null | undefined) {
  const context = useContext(BreadcrumbLabelContext);
  const setLabel = context?.setLabel;
  const { pathname } = useLocation();

  useEffect(() => {
    if (!setLabel) return;

    if (label) setLabel(pathname, label);
    return () => setLabel(pathname, null);
  }, [label, pathname, setLabel]);
}

export function useCurrentBreadcrumbLabel(pathname: string) {
  const context = useContext(BreadcrumbLabelContext);
  return context?.current?.pathname === pathname ? context.current.label : undefined;
}
